import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import PaymentForm from './PaymentForm';
import { GuestPaymentContext } from './paymentForm/paymentApiContext';
import { createGuestPaymentApi, validateBuyerDetails } from './paymentForm/guestPaymentApi';

// New students pay on the same payment form existing students use (courses or products).
// This only supplies what a student without an account needs: the email from the
// enrollment step (where it decided new-vs-existing), the details typed on the form, and
// a guest API for the form to use instead of the signed-in one. Nothing is created until
// Stripe confirms payment.
// Typed details survive a trip back to confirm the email again (the proof lapses after 30
// minutes) or a refresh. Per tab, never kept after the purchase.
export const DETAILS_STORAGE_PREFIX = 'guestCheckoutDetails:';
const EMPTY_DETAILS = { firstName: '', lastName: '', phone: '' };
const loadDetails = (email) => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(DETAILS_STORAGE_PREFIX + email) || 'null');
    return saved && typeof saved === 'object' ? { ...EMPTY_DETAILS, ...saved } : EMPTY_DETAILS;
  } catch {
    return EMPTY_DETAILS;
  }
};

function GuestPayment() {
  const { courseIds, productIds } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, applySession } = useAuth();

  const email = location.state?.email || '';
  // Proof the email was confirmed with an emailed code, earned on the enrollment step.
  const emailToken = location.state?.emailToken || '';
  const [details, setDetails] = useState(() => loadDetails(email));
  const [detailsError, setDetailsError] = useState('');

  // The adapter reads the latest values through refs so it can stay one stable object.
  const emailRef = useRef(email);
  const emailTokenRef = useRef(emailToken);
  const detailsRef = useRef(details);
  const applySessionRef = useRef(applySession);
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  emailRef.current = email;
  emailTokenRef.current = emailToken;
  detailsRef.current = details;

  useEffect(() => {
    if (!email) return;
    try {
      sessionStorage.setItem(DETAILS_STORAGE_PREFIX + email, JSON.stringify(details));
    } catch { /* storage unavailable: the details just aren't remembered */ }
  }, [email, details]);
  applySessionRef.current = applySession;

  const flowPath = courseIds ? `/enroll/flow/${courseIds}` : `/enroll/products/${productIds}`;
  const flowPathRef = useRef('');
  flowPathRef.current = courseIds ? `/enroll/flow/${courseIds}` : `/enroll/products/${productIds}`;
  const signedInPath = courseIds ? `/enroll/multiple/${courseIds}` : `/purchase/products/${productIds}`;

  useEffect(() => {
    if (isAuthenticated) {
      navigate(signedInPath, { replace: true }); // already a student: the regular page
    } else if (!email || !emailToken) {
      navigate(flowPath, { replace: true }); // the email step decides new vs existing and confirms the address
    }
  }, [isAuthenticated, email, emailToken, flowPath, signedInPath, navigate]);

  const guestApi = useMemo(() => createGuestPaymentApi({
    getEmail: () => emailRef.current,
    getEmailToken: () => emailTokenRef.current,
    getDetails: () => detailsRef.current,
    applySession: (result) => applySessionRef.current(result),
    // the 30-minute proof lapsed: confirm the email again
    onEmailVerificationRequired: () => navigateRef.current(flowPathRef.current, { replace: true }),
    onAccountExists: () => navigateRef.current(`/login?redirect=${encodeURIComponent(flowPathRef.current)}`, { replace: true })
  }), []);

  const value = useMemo(() => ({
    api: guestApi,
    email,
    details,
    // Editing clears the inline message; the form blocks retries while *its* error is set,
    // so problems with these fields are shown on the details card instead.
    setDetails: (update) => { setDetails(update); setDetailsError(''); },
    detailsError,
    // true when the details are good to start a payment; otherwise shows why on the card.
    validateDetails: () => {
      const problem = validateBuyerDetails(detailsRef.current);
      setDetailsError(problem);
      return !problem;
    },
    changeEmail: () => navigate(flowPath),
    // Where Stripe sends the buyer after paying; finalizes enrollment and signs them in.
    returnUrl: `${window.location.origin}/portal/enroll/guest/complete`
  }), [guestApi, email, details, detailsError, navigate, flowPath]);

  if (isAuthenticated || !email || !emailToken) return null;

  return (
    <GuestPaymentContext.Provider value={value}>
      <PaymentForm />
    </GuestPaymentContext.Provider>
  );
}

export default GuestPayment;
